/**
 * feat-107 Slice A — center pane: the selected Drill + its child list.
 *
 * Top: a `Lesson › Drill` breadcrumb (level word prefixed, from QUIZ_TERMS).
 * Then a compact Drill meta strip — icon swatch (IconPickerPopover, the killed
 * 12-tile grid), inline-editable title, ★XP chip, Qs/session field, Active pill,
 * Delete link. Then the flat list of child Questions + Rehearsals with type
 * badges, ★XP chips, a ⋯ menu, and drag-to-reorder. Empty (c) renders when the
 * Drill has no items. All chrome is consolidated here (declutter win #2) — the
 * only per-item Save lives in the right detail footer.
 */

import React, { useEffect, useRef, useState } from 'react';
import type {
  QuizUnit,
  QuizLesson,
  QuizQuestion,
  RehearsalLessonItem,
  VideoItemSummary,
  ReadingItemSummary,
} from '../../../services/quizContentAdminService';
import { LESSON_ICON_CATALOG } from '../../../services/quizContentAdminService';
import {
  QUIZ_TERMS,
  QUESTION_TYPE_BADGE,
  REHEARSAL_BADGE,
  VIDEO_BADGE,
  READING_BADGE,
} from '../quizTerminology';
import { IconPickerPopover, IconSelection } from '../IconPickerPopover';
import ContentTimestamps from './ContentTimestamps';
import { MOVE_DRILL_COPY } from './moveDrillCopy';

export type DrillItem =
  | { kind: 'question'; q: QuizQuestion }
  | { kind: 'rehearsal'; r: RehearsalLessonItem }
  // feat-165: explanatory video item — interleaves with questions/rehearsals at
  // its own sort_order and carries an async captions_status chip.
  | { kind: 'video'; v: VideoItemSummary }
  // feat-166: reading item — interleaves at its own sort_order; no XP, no status.
  | { kind: 'reading'; rd: ReadingItemSummary };

export type DrillMetaPatch = Partial<{
  title: string;
  icon_key: string | null;
  emoji: string;
  xp_reward: number;
  question_count: number | null;
  active: boolean;
}>;

export interface DrillChildListProps {
  unit: QuizUnit;
  drill: QuizLesson;
  items: DrillItem[];
  /** `question:<id>` / `rehearsal:<id>` of the selected row, or null. */
  selectedItemKey: string | null;
  onSelectItem: (item: DrillItem) => void;
  onSelectLessonCrumb: () => void;
  glyphForKey: (key: string) => string;
  onSaveDrillMeta: (patch: DrillMetaPatch) => Promise<void>;
  onDeleteDrill: () => void;
  /** feat-109: one affordance — opens the detail pane where the admin picks the
   *  type (Question / Rehearsal) via the Type control. */
  onAdd: () => void;
  onReorderItems: (orderedKeys: string[]) => void;
  /** feat-361 — opens the "Move to Lesson…" picker. Absent ⇒ no Move button. */
  onMoveDrill?: () => void;
  /** feat-361 — the untimed success strip after a move (NOTES §5); null ⇒ none. */
  moveNotice?: { lessonTitle: string } | null;
  onDismissMoveNotice?: () => void;
}

const itemKey = (item: DrillItem): string =>
  item.kind === 'question'
    ? `question:${item.q.id}`
    : item.kind === 'rehearsal'
      ? `rehearsal:${item.r.id}`
      : item.kind === 'video'
        ? `video:${item.v.id}`
        : `reading:${item.rd.id}`;

function itemBadge(item: DrillItem): { label: string; cls: string } {
  if (item.kind === 'rehearsal') {
    return { label: REHEARSAL_BADGE, cls: 'bg-coral-light text-coral' };
  }
  if (item.kind === 'video') {
    return { label: VIDEO_BADGE, cls: 'bg-video-light text-video' };
  }
  if (item.kind === 'reading') {
    return { label: READING_BADGE, cls: 'bg-reading-light text-reading' };
  }
  const isFill = item.q.question_type === 'fill_blank';
  return {
    label: QUESTION_TYPE_BADGE[item.q.question_type] ?? 'MC',
    cls: isFill ? 'bg-preview-light text-preview' : 'bg-blue-50 text-blue-700',
  };
}
function itemText(item: DrillItem): string {
  if (item.kind === 'question') return item.q.question_text || '(no text)';
  if (item.kind === 'rehearsal') return item.r.name || '(unnamed rehearsal)';
  if (item.kind === 'reading') return item.rd.title || '(untitled reading)';
  return item.v.title || '(untitled video)';
}
function itemXp(item: DrillItem): number | null {
  // Video + reading items grant no XP (feat-144 behavior) — no ★ chip.
  if (item.kind === 'video' || item.kind === 'reading') return null;
  return item.kind === 'question' ? item.q.xp_value : item.r.xp_value;
}

/** feat-165: compact captions_status chip for a video row. The captions_status is
 *  backend-authoritative (never classified in the FE); tones use the semantic
 *  family — success/warning/danger, never brand coral.
 *
 *  feat-240: the success tone is `text-success-on`, which DIVERGES from StatusChip's
 *  `text-success` on purpose. StatusChip has not been migrated yet and is a known
 *  WCAG AA failure at 2.589:1 (see .team/programs/success-token-text-contrast.md).
 *  Do NOT "restore consistency" by reverting this to `text-success` — that
 *  reintroduces the defect. Consistency is restored by fixing StatusChip, not this. */
function videoStatusChip(
  v: VideoItemSummary,
): { label: string; cls: string; spinning: boolean } {
  if (v.captions_status === 'pending') {
    return { label: 'Generating captions…', cls: 'bg-warning-surface text-warning-on', spinning: true };
  }
  if (v.captions_status === 'failed') {
    return { label: 'Captions failed', cls: 'bg-danger-surface text-danger', spinning: false };
  }
  // ready
  if (v.published) {
    return { label: 'Published', cls: 'bg-success-light text-success-on', spinning: false };
  }
  return { label: 'Draft · ready to publish', cls: 'bg-warning-surface text-warning-on', spinning: false };
}

export const DrillChildList: React.FC<DrillChildListProps> = ({
  unit,
  drill,
  items,
  selectedItemKey,
  onSelectItem,
  onSelectLessonCrumb,
  glyphForKey,
  onSaveDrillMeta,
  onDeleteDrill,
  onAdd,
  onReorderItems,
  onMoveDrill,
  moveNotice = null,
  onDismissMoveNotice,
}) => {
  const [title, setTitle] = useState(drill.title);
  const [qs, setQs] = useState<string>(drill.question_count == null ? '' : String(drill.question_count));
  const [xp, setXp] = useState<string>(String(drill.xp_reward));
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const moveButtonRef = useRef<HTMLButtonElement>(null);

  // feat-361 NOTES §4 — after a move lands, focus returns to the invoking
  // button. The pane may have remounted during the reload, so the dialog's own
  // restore can find a detached node; this is the reliable path.
  useEffect(() => {
    if (moveNotice) moveButtonRef.current?.focus();
  }, [moveNotice]);

  // feat-231: now that the item list is its own scroll container, it KEEPS its
  // scroll offset across a Drill change (this component re-renders, it does not
  // remount). Selecting a new Drill would drop you into the middle of its item
  // list. Reset on `drill.id` only — not on the meta fields below, or an inline
  // title/XP edit would jump the list to the top mid-scroll.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [drill.id]);

  // Re-sync local fields when a different Drill is selected.
  useEffect(() => {
    setTitle(drill.title);
    setQs(drill.question_count == null ? '' : String(drill.question_count));
    setXp(String(drill.xp_reward));
  }, [drill.id, drill.title, drill.question_count, drill.xp_reward]);

  useEffect(() => {
    if (!menuFor) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuFor(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuFor]);

  const iconSelection: IconSelection | null = drill.icon_key
    ? { kind: 'catalog', key: drill.icon_key }
    : drill.emoji
      ? { kind: 'custom', emoji: drill.emoji }
      : null;

  const commitTitle = () => {
    const next = title.trim();
    if (next && next !== drill.title) onSaveDrillMeta({ title: next });
    else setTitle(drill.title);
  };
  const commitQs = () => {
    const trimmed = qs.trim();
    const next = trimmed === '' ? null : Math.max(0, parseInt(trimmed, 10) || 0);
    if (next !== drill.question_count) onSaveDrillMeta({ question_count: next });
  };
  const commitXp = () => {
    const next = Math.max(0, parseInt(xp.trim(), 10) || 0);
    if (next !== drill.xp_reward) onSaveDrillMeta({ xp_reward: next });
    else setXp(String(drill.xp_reward));
  };

  const reorder = (targetKey: string) => {
    if (!dragKey || dragKey === targetKey) return;
    const keys = items.map(itemKey);
    const from = keys.indexOf(dragKey);
    const to = keys.indexOf(targetKey);
    if (from < 0 || to < 0) return;
    keys.splice(to, 0, keys.splice(from, 1)[0]);
    onReorderItems(keys);
  };

  return (
    // feat-231: the SECTION no longer scrolls. If it did, the breadcrumb and the
    // drill meta strip (title, ★XP, Qs/session, Active, Delete Drill) would slide
    // away with the item list — the pane would still scroll independently, but
    // you would lose your place in exactly the way the bug report describes.
    // Scrolling moves to the item list below; everything above it is pinned.
    <section className="flex flex-col min-h-0 overflow-hidden">
      {/* breadcrumb — pinned */}
      <div className="px-6 pt-5 shrink-0 flex items-center gap-2 text-sm">
        <span className="text-inkMuted hover:text-coral cursor-pointer truncate max-w-[40%]" onClick={onSelectLessonCrumb}>
          <span className="text-inkMuted/60 text-xs mr-1">{QUIZ_TERMS.lesson.singular}</span>
          {unit.title || '(untitled)'}
        </span>
        <span className="text-inkMuted/50">›</span>
        <span className="text-ink font-bold truncate">
          <span className="text-inkMuted/60 text-xs mr-1 font-normal">{QUIZ_TERMS.drill.singular}</span>
          {drill.title || '(untitled)'}
        </span>
        {/* feat-252: the breadcrumb is the Drill's identity block and has free
            space on its right (both crumbs already truncate). A 12px child in
            this `text-sm` row does not grow it, so the pinned meta strip below
            stays exactly as tall as it is today (NOTES §3). */}
        <ContentTimestamps variant="inline" created={drill.created} />
      </div>

      {/* Drill meta strip — pinned */}
      <div className="px-6 pt-3 shrink-0">
        <div className="bg-cream/70 border border-softBorder rounded-2xl p-3 flex items-center gap-3 flex-wrap">
          <IconPickerPopover
            catalog={LESSON_ICON_CATALOG}
            selection={iconSelection}
            glyphForKey={glyphForKey}
            ariaLabel={`Change ${QUIZ_TERMS.drill.singular} icon`}
            onSelect={(sel) =>
              sel.kind === 'catalog'
                ? onSaveDrillMeta({ icon_key: sel.key })
                : onSaveDrillMeta({ emoji: sel.emoji, icon_key: null })
            }
          />

          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            className="flex-1 min-w-[180px] bg-transparent font-display text-lg font-semibold px-1 py-1 rounded-lg hover:bg-white focus:bg-white focus:outline-none focus:ring-2 focus:ring-coral/40"
          />

          {/* ★XP chip (editable) */}
          <label className="bg-gold-light text-gold text-xs font-bold rounded-full pl-2.5 pr-1 py-0.5 flex items-center gap-1" title={`${QUIZ_TERMS.drill.singular} XP reward`}>
            ★
            <input
              value={xp}
              onChange={(e) => setXp(e.target.value)}
              onBlur={commitXp}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
              className="w-10 text-center bg-transparent text-gold font-bold focus:outline-none"
            />
            <span className="pr-1.5">XP</span>
          </label>

          <label className="flex items-center gap-1.5 text-xs text-inkMuted">
            <span>Qs / session</span>
            <input
              value={qs}
              placeholder="all"
              onChange={(e) => setQs(e.target.value)}
              onBlur={commitQs}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
              className="w-12 text-center bg-white border border-softBorder rounded-lg px-1 py-1 text-ink font-semibold focus:outline-none focus:ring-2 focus:ring-coral/40"
            />
          </label>

          <button
            type="button"
            onClick={() => onSaveDrillMeta({ active: !drill.active })}
            className={`text-xs font-bold rounded-full px-2.5 py-1 flex items-center gap-1.5 ${
              drill.active ? 'bg-success-light text-success-on' : 'bg-gray-100 text-inkMuted'
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${drill.active ? 'bg-success' : 'bg-inkMuted'}`} />
            {drill.active ? 'Active' : 'Hidden'}
          </button>

          {/* feat-361 NOTES §6 — Move + Delete share ONE wrapper so the pair wraps
              as a unit; without it Delete alone drops to a second line. */}
          <div className="flex items-center gap-3 shrink-0">
            {onMoveDrill && (
              <button
                ref={moveButtonRef}
                type="button"
                aria-haspopup="dialog"
                onClick={onMoveDrill}
                data-testid="drill-move-button"
                className="text-inkMuted text-sm font-semibold hover:text-ink hover:underline rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
              >
                {MOVE_DRILL_COPY.action}
              </button>
            )}
            <button type="button" onClick={onDeleteDrill} className="text-coral text-sm font-semibold hover:underline">
              Delete {QUIZ_TERMS.drill.singular}
            </button>
          </div>
        </div>

        {/* feat-361 NOTES §5 — untimed success strip, pinned under the meta strip so
            it never scrolls away. No timer, no undo (revert lives in History). */}
        {moveNotice && (
          <div
            role="status"
            data-testid="drill-move-notice"
            className="mt-2 bg-success-light text-success-on rounded-xl px-3 py-2 text-sm flex items-center gap-2"
          >
            <svg className="w-4 h-4 shrink-0" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6 9 17l-5-5" />
            </svg>
            <span className="flex-1 min-w-0 break-words">
              {MOVE_DRILL_COPY.movedTo}
              <strong className="font-semibold">{moveNotice.lessonTitle || '(untitled)'}</strong>
              {MOVE_DRILL_COPY.movedSuffix}
            </span>
            <button
              type="button"
              aria-label={MOVE_DRILL_COPY.dismiss}
              onClick={onDismissMoveNotice}
              className="shrink-0 p-1 rounded-lg hover:bg-white/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
            >
              <svg className="w-3.5 h-3.5" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* child list — the ONLY scrolling region of this pane */}
      <div ref={listRef} className="px-6 py-4 flex-1 min-h-0 overflow-y-auto">
        {items.length === 0 ? (
          <div className="bg-white border border-softBorder rounded-3xl p-8 text-center mt-2">
            <div className="text-5xl mb-3">✍️</div>
            <h4 className="font-display text-lg font-semibold">
              No {QUIZ_TERMS.question.plural} or {QUIZ_TERMS.rehearsal.plural}
            </h4>
            <p className="text-sm text-inkMuted mt-1 mb-4">
              Add a {QUIZ_TERMS.question.singular} or a spoken {QUIZ_TERMS.rehearsal.singular} to this {QUIZ_TERMS.drill.singular}.
            </p>
            <div className="flex justify-center gap-2">
              <button type="button" onClick={onAdd} className="bg-coral text-white font-semibold rounded-full px-4 py-2 text-sm hover:brightness-95">
                + Add
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="text-xs font-bold text-inkMuted uppercase tracking-wide mb-2 px-1">
              {items.length} {items.length === 1 ? 'item' : 'items'}
            </div>
            <div className="space-y-1.5">
              {items.map((item) => {
                const key = itemKey(item);
                const selected = key === selectedItemKey;
                const badge = itemBadge(item);
                const xpVal = itemXp(item);
                return (
                  <div
                    key={key}
                    draggable
                    onDragStart={() => setDragKey(key)}
                    onDragEnd={() => setDragKey(null)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => reorder(key)}
                    onClick={() => onSelectItem(item)}
                    className={`listrow group flex items-center gap-3 rounded-2xl px-3 py-2.5 cursor-pointer ${
                      selected ? 'bg-coral-light border border-coral/30' : 'bg-white border border-softBorder hover:bg-cream/60'
                    }`}
                  >
                    <span className="drag opacity-0 group-hover:opacity-100 transition-opacity text-inkMuted/40 text-xs select-none cursor-grab">⠿</span>
                    <span className={`text-[10px] font-bold rounded-md px-2 py-1 shrink-0 ${badge.cls}`}>{badge.label}</span>
                    <span className={`flex-1 truncate text-sm font-semibold ${selected ? 'text-ink' : ''}`}>{itemText(item)}</span>
                    {item.kind === 'video' &&
                      (() => {
                        const chip = videoStatusChip(item.v);
                        return (
                          <span
                            className={`inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2 py-0.5 shrink-0 ${chip.cls}`}
                          >
                            {chip.spinning ? (
                              <span className="w-2.5 h-2.5 rounded-full border-2 border-warning-on border-t-transparent animate-spin inline-block" />
                            ) : (
                              <span className="w-1.5 h-1.5 rounded-full bg-current" />
                            )}
                            {chip.label}
                          </span>
                        );
                      })()}
                    {xpVal != null && (
                      <span className="bg-gold-light text-gold text-[10px] font-bold rounded-full px-2 py-0.5 shrink-0">★{xpVal}</span>
                    )}
                    <div className="relative shrink-0" ref={menuFor === key ? menuRef : undefined}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuFor(menuFor === key ? null : key);
                        }}
                        className="text-inkMuted hover:text-coral hover:bg-coral-light rounded-lg p-1.5"
                      >
                        ⋯
                      </button>
                      {menuFor === key && (
                        <div className="absolute right-0 top-9 z-20 w-32 bg-white border border-softBorder rounded-xl shadow-xl py-1 text-sm">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuFor(null);
                              onSelectItem(item);
                            }}
                            className="w-full text-left px-3 py-1.5 hover:bg-gray-100"
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex gap-2 mt-4">
              <button type="button" onClick={onAdd} className="bg-coral text-white font-semibold rounded-full px-4 py-2 text-sm hover:brightness-95">
                + Add
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default DrillChildList;
